import { useState } from "react";

export interface Filters {
  type: string;
  region: string;
  year: string;
  sort: string;
}

interface FilterBarProps {
  filters: Filters;
  onChange: (key: keyof Filters, value: string) => void;
  onReset: () => void;
  resultCount: number;
}

const typeOptions = ["全部", "电影", "电视剧", "网络剧", "短片"];
const regionOptions = ["全部", "中国大陆", "中国香港", "韩国", "日本", "美国"];
const yearOptions = ["全部", "2026", "2025", "2024", "2023", "2022"];
const sortOptions = [
  { value: "latest", label: "最新上映" },
  { value: "popular", label: "热门度" },
  { value: "rating", label: "评分最高" },
];

function ChipGroup({
  options,
  value,
  onSelect,
}: {
  options: { value: string; label: string }[];
  value: string;
  onSelect: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onSelect(opt.value)}
          className={`whitespace-nowrap rounded-md border px-3 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
            value === opt.value
              ? "border-primary-500 bg-primary-500 text-background-50"
              : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function FilterBar({ filters, onChange, onReset, resultCount }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const rows = (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">类型</span>
        <ChipGroup
          options={typeOptions.map((v) => ({ value: v, label: v }))}
          value={filters.type}
          onSelect={(v) => onChange("type", v)}
        />
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">地区</span>
        <ChipGroup
          options={regionOptions.map((v) => ({ value: v, label: v }))}
          value={filters.region}
          onSelect={(v) => onChange("region", v)}
        />
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">年份</span>
        <ChipGroup
          options={yearOptions.map((v) => ({ value: v, label: v }))}
          value={filters.year}
          onSelect={(v) => onChange("year", v)}
        />
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">排序</span>
        <ChipGroup options={sortOptions} value={filters.sort} onSelect={(v) => onChange("sort", v)} />
      </div>
    </div>
  );

  return (
    <div className="rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5">
      <div className="flex items-center justify-between md:hidden">
        <div className="flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-500" />
          <span className="text-sm font-medium text-foreground-800">筛选条件</span>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 cursor-pointer"
        >
          {open ? "收起" : "展开"}
          <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
        </button>
      </div>

      <div className="hidden md:block">{rows}</div>
      {open && <div className="mt-4 md:hidden">{rows}</div>}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
        <p className="text-sm text-foreground-500">
          共找到 <span className="font-semibold text-primary-600">{resultCount}</span> 部作品
        </p>
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
        >
          <i className="ri-refresh-line" />
          重置筛选
        </button>
      </div>
    </div>
  );
}